import { Component, Input, SimpleChanges, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CryptoService } from 'src/app/services/crypto.service';
import { DataService } from 'src/app/services/data.service';
import { GlobalConstants } from 'src/app/services/global-constants';
import { PermissionHandlerService } from 'src/app/services/permission-handler.service';
import { PermissionLoaderService } from 'src/app/services/permission-loader.service';

@Component({
  selector: 'app-home-searchbar',
  templateUrl: './home-searchbar.component.html',
  styleUrls: ['./home-searchbar.component.scss']
})
export class HomeSearchbarComponent implements OnInit, OnDestroy {
  searchText = '';
  @Input() ous: any;
  @Input() pageLoading = false;
  filteredOus: any;
  recentsearches: any = [];
  serviceCounts: any = undefined;
  showServiceCount: boolean = false;
  visibleModal: boolean = false;
  staticURL: string = GlobalConstants.staticFilesURL;
  currentFieldType: any;
  
  // Hero slideshow, crossfading every 4 seconds.
  backgroundImages = [
    '../../../assets/icones-2025/caro-3.jpeg',
    '../../../assets/icones-2025/caro-4.jpeg',
    '../../../assets/icones-2025/caro-1.jpeg'
  ];
  currentBackgroundIndex = 0;
  private backgroundTimer: any;
  // 'all', or the Arabic type name search-view filters its results on.
  searchType: string = 'all';
  // Set when the counts request fails, so the skeleton gives way to nothing.
  countsFailed = false;

  constructor(
    private router: Router,
    private dataService: DataService,
    private cryptoService: CryptoService,
    private permissionService: PermissionHandlerService,
    private permisssionLoader: PermissionLoaderService
  ) { }

  isLoggedIn = JSON.parse(localStorage.getItem('isLoggedIn') || "false");

  // Skeletons stand in for the chips and count cards until the counts arrive.
  // Logged-in counts also wait for the entities list, which only loads with
  // CONTENT read, so without it there is nothing to wait for.
  get countsPending(): boolean {
    if (this.pageLoading) return true;
    if (this.countsFailed || this.showServiceCount) return false;
    return !this.isLoggedIn || this.hasPermission('CONTENT', 'r');
  }

  get searchDisabled(): boolean {
    return this.isLoggedIn && !this.hasPermission('CONTENT', 'r');
  }

  // The two leading types (services, FAQs) offered as chips under the search.
  get quickTypes(): any[] {
    return this.serviceCounts ? this.dataSorter([...this.serviceCounts]).slice(0, 2) : [];
  }

  // Every type with a count, services and FAQs first, as the old page had.
  get statTypes(): any[] {
    return this.serviceCounts ? this.dataSorter([...this.serviceCounts]) : [];
  }

  submitSearch() {
    this.navigateToSearch({ target: { value: this.searchText } });
  }

  navigateToSearch(event: any) {
    let search: string = event.target.value;
    if (search.includes("\"") || search.includes("+") || search.includes("-")) {
      this.advancedSearch(search);
      return;
    }
    let queryParams: any = { searchTerm: search };
    if (this.searchType !== 'all') {
      queryParams.type = this.searchType;
    }
    this.router.navigate(['/search-view'], { queryParams })
  }

  statIcon(item: any): string {
    switch (item?.type?.arabic) {
      case 'الخدمات': return 'pi-cog';
      case 'الأسئلة الشائعة': return 'pi-question-circle';
      default: return item?.type?.icon || 'pi-file';
    }
  }

  // The singular noun shown after a count, e.g. "714 خدمة".
  statUnit(item: any): string {
    switch (item?.type?.arabic) {
      case 'الخدمات': return 'خدمة';
      case 'الأسئلة الشائعة': return 'سؤال';
      default: return '';
    }
  }

  ngOnInit() {
    this.backgroundTimer = setInterval(() => {
      this.currentBackgroundIndex = (this.currentBackgroundIndex + 1) % this.backgroundImages.length;
    }, 4000);
    this.fetchRecentSearches();
    if (!this.isLoggedIn) {
      this.permissionLoaderSub = this.permisssionLoader.getState().subscribe(e => {
        if (!e) {
          this.fetchServiceCount();
        }
      })
      // Already loaded before this page opened, so no "done" will follow.
      if (this.permisssionLoader.isLoaded) {
        this.fetchServiceCount();
      }
    } else {
      this.fetchServiceCount();
    }
  }

  advancedSearch(search: string) {
    let query: any = { condition: 'and', rules: [] };
    query.extras = this.extractSearchParts(search);
    let encryptedQuery = this.cryptoService.encodeObject(query);
    console.log('QUERY', query);
    let params = {
      query: encryptedQuery,
      searchTerm: encodeURI(search || '')
    };
    this.router.navigate(['/advance-search/result'], { queryParams: params })
  }

  extractSearchParts(search: string) {
    const removal: string[] = [];
    const exact: string[] = [];
    const any: string[] = [];

    // Extract exclusion phrases first (e.g., -"some text")
    search = search.replace(/(^|\s)-\s*"([^"]+)"/g, (_, prefix: string, match: string) => {
      removal.push(match.trim());
      return prefix ? `${prefix} ` : ' ';
    });

    // Extract exclusion single words (e.g., -word or - word)
    search = search.replace(/(^|\s)-\s*([^\s"]+)/g, (_, prefix: string, match: string) => {
      removal.push(match.trim());
      return prefix ? `${prefix} ` : ' ';
    });

    // Extract exact match parts that remain
    search = search.replace(/"([^"]+)"/g, (_, match: string) => {
      exact.push(match.trim());
      return ' ';
    });

    // Remaining tokens act as "all words" search, ignore explicit "+"
    const remainingParts = search.replace(/\+/g, ' ').trim();
    if (remainingParts) {
      remainingParts.split(/\s+/).forEach(part => {
        if (part.trim()) {
          any.push(part.trim());
        }
      });
    }

    const query: any = {};
    if (any.length) {
      query.likeAll = any.join(' ');
    }
    if (exact.length) {
      query.sameWord = exact.join(' ').replaceAll('+', '');
    }
    if (removal.length) {
      query.notContains = removal.join(' ').replaceAll('+', '');
    }
    return query;
  }

  searchItem(text: string) {
    this.searchText = text;
    this.navigateToSearch({ target: { value: this.searchText } })
  }

  hasPermission(unit: string, flag: string) {
    return this.permissionService.hasPermission(unit, flag)
  }

  checkisImg(arg0: string | undefined): any {
    if (arg0) {
      return /\.(jpg|jpeg|png|gif|svg)/.test(arg0);
    } else {
      return false
    }
  }

  async fetchRecentSearches() {
    try {
      let res = await this.dataService.getData('ou/search-history');
      this.recentsearches = res.data.reverse();
      if (this.recentsearches.length > 10) {
        this.recentsearches = this.recentsearches.splice(0, 10)
      }
    } catch (e) {
      console.log(e);
    }
  }

  async fetchServiceCount() {
    // A guest's first load reports done before the role is in; that attempt
    // fails, and the retry after the real load should still count.
    this.countsFailed = false;
    try {
      console.log("isloggedin ==>", this.isLoggedIn);
      if (this.isLoggedIn) {
        let res = await this.dataService.getData('data/service-count');
        if (res.error) {
          this.countsFailed = true;
          return
        }
        this.serviceCounts = res.data;
        console.log('service count', this.serviceCounts);
        // The entities may have arrived first, in which case ngOnChanges found
        // no counts to work on.
        if (this.ous) {
          this.calculateCounts();
        }
      } else {
        let signedOus = this.permissionService.payload.content.signedOus.content;
        let allOus = this.permissionService.payload.content.allOus.content;
        let payload = {
          signed: signedOus,
          all: allOus
        };

        console.log("PAYLOAD ===>>>", payload);
        let res = await this.dataService.postData('data/service-count', payload);
        if (res.error) {
          this.countsFailed = true;
          return
        } else {

          let dataArray = res.data;
          // Create a map to store the aggregated data
          const aggregatedMap = new Map<string, any>();

          console.log("check before the table")
          await Promise.all(dataArray.map(async (item: { _id: string; count: any; signed: any; }) => {
            if (aggregatedMap.has(item._id)) {
              const existingItem = aggregatedMap.get(item._id);
              if (existingItem) {
                let check = signedOus.includes(item._id)
                console.log("ou in table check", check, signedOus, item._id)

                if (signedOus.includes(item._id)) {
                  existingItem.count += item.signed;
                } else {
                  existingItem.count += item.count;
                }
              }
            } else {
              aggregatedMap.set(item._id, { ...item });
            }
          }));

          // Convert the map back to an array
          const resultArray = Array.from(aggregatedMap.values());

          this.serviceCounts = resultArray;

          console.log('service count if not logged in', this.serviceCounts);
        }
        this.showServiceCount = true;

      }
    } catch (e) {
      this.countsFailed = true;
      console.log(e);
    }
  }

  calculateCounts() {
    if (this.serviceCounts) {
      if (this.isLoggedIn) {

        this.serviceCounts = this.serviceCounts.map((s: any) => {
          let count = 0;
          this.ous.forEach((ou: any) => {
            let counter = ou.data_counter;
            if (counter.length > 0) {
              let found = counter.find((c: any) => c._id == s.type.arabic);
              if (found) {
                count += found.count;
              }
            }
          });
          s.count = count;
          return s;
        }).filter((s: any) => s.count != 0);
        this.serviceCounts = this.dataSorter(this.serviceCounts);
        this.showServiceCount = true;
      }
    }
  }

  dataSorter(dataCounter: any[]): any[] {
    const orderArray = ["الخدمات", "الأسئلة الشائعة"];

    // Sort the dataCounter array
    dataCounter.sort((a, b) => {
      const aIndex = orderArray.indexOf(a.type.arabic);
      const bIndex = orderArray.indexOf(b.type.arabic);

      // If both items are in the orderArray, sort based on their order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only one of the items is in the orderArray, it comes first
      if (aIndex !== -1) {
        return -1;
      }
      if (bIndex !== -1) {
        return 1;
      }

      // If neither item is in the orderArray, sort them by their _id
      return a.type.arabic.localeCompare(b.type.arabic);
    });

    return dataCounter; // Return the sorted array
  }

  openLicenseModal(item: any) {
    this.currentFieldType = item;
    console.log(this.currentFieldType);
    console.log(this.filteredOus);
    if (this.currentFieldType) {
      this.filteredOus = this.ous.filter((ou: any) => {
        let counters = ou.data_counter;
        if (counters.length == 0) {
          return false;
        }
        let present = counters.find((c: any) => c._id == item.type.arabic);
        if (present) {
          return true;
        }
        return false;
      })
      this.visibleModal = true;
    }
  }

  ngOnChanges(simpleChange: SimpleChanges) {
    if (simpleChange['ous']) {
      this.ous = simpleChange['ous'].currentValue;
      this.filteredOus = this.ous;
      this.calculateCounts();
    }
  }

  getCount(ou: any) {

    if (this.isLoggedIn) {

      return ou.data_counter.find((d: any) => d._id == this.currentFieldType.type.arabic)?.count

    } else {
      let signedOus = this.permissionService.payload.content.signedOus.all;
      if (signedOus.includes(ou._id)) {
        return ou.data_counter.find((d: any) => d._id == this.currentFieldType.type.arabic)?.signed
      } else {
        return ou.data_counter.find((d: any) => d._id == this.currentFieldType.type.arabic)?.count
      }

    }

  }

  testCount() {
    let ouCount = 0
    this.filteredOus.forEach((ou: any) => {
      ouCount += this.getCount(ou);
    })
    return ouCount
  }

  navigateToResult(ou: any) {
    if (this.currentFieldType) {
      let query: any = {
        condition: 'and',
        rules: [],
        extras: { ou: ou.id, type: this.currentFieldType._id }
      };
      let encryptedQuery = this.cryptoService.encodeObject(query);
      let queryParams = { query: encryptedQuery };
      this.router.navigate(['/advance-search/result'], { queryParams });
    }
  }

  private permissionLoaderSub?: Subscription;

  ngOnDestroy() {
    this.permissionLoaderSub?.unsubscribe();
    clearInterval(this.backgroundTimer);
  }
}
